if (!customElements.get('zayix-collection-scroll')) {
  class ZayixCollectionScroll extends HTMLElement {
    connectedCallback() {
      this.grid = this.closest('.collection')?.querySelector('ul#product-grid');
      this.button = this.querySelector('button');
      this.status = this.querySelector('[role="status"]');
      if (!this.grid || !this.button || !('IntersectionObserver' in window)) return;
      this.pageKey = window.location.pathname + window.location.search;
      this.pages = 1;
      this.loading = false;
      this.failed = false;
      this.controller = new AbortController();
      this.previousAnchor = document.documentElement.style.overflowAnchor;
      document.documentElement.style.overflowAnchor = 'none';
      this.hidden = false;
      this.style.display = 'block';
      this.closest('.collection').querySelector('.pagination-wrapper')?.setAttribute('hidden', '');
      this.button.addEventListener('click', () => { this.failed = false; this.load(); }, { signal: this.controller.signal });
      this.savePlace = (event) => {
        if (!event.target.closest('a[href*="/products/"]')) return;
        try { sessionStorage.setItem('zayix-scroll:' + this.pageKey, JSON.stringify({ pages: this.pages, y: scrollY, at: Date.now() })); } catch (_) {}
      };
      this.grid.addEventListener('click', this.savePlace, { signal: this.controller.signal });
      this.observer = new IntersectionObserver((entries) => {
        if (entries.some(entry => entry.isIntersecting) && !this.failed && !this.restoring) this.load();
      }, { rootMargin: '600px 0px' });
      this.restorePlace();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      document.documentElement.style.overflowAnchor = this.previousAnchor || '';
    }

    async restorePlace() {
      let saved;
      try { saved = JSON.parse(sessionStorage.getItem('zayix-scroll:' + this.pageKey)); sessionStorage.removeItem('zayix-scroll:' + this.pageKey); } catch (_) {}
      if (saved && Date.now() - saved.at < 1800000 && saved.pages > 1) {
        this.restoring = true;
        while (this.isConnected && this.pages < saved.pages && this.dataset.next && !this.failed) await this.load();
        if (this.isConnected && !this.failed) {
          await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
          scrollTo({ top: saved.y, behavior: 'instant' });
        }
        this.restoring = false;
      }
      if (this.isConnected && this.dataset.next) this.observer.observe(this);
    }

    async load() {
      if (this.loading || !this.dataset.next || !this.isConnected) return;
      this.loading = true;
      this.button.disabled = true;
      this.status.textContent = this.dataset.loading;
      this.grid.setAttribute('aria-busy', 'true');
      const next = new URL(this.dataset.next, window.location.origin);
      const current = new URL(window.location.href);
      for (const [key, value] of current.searchParams) {
        if (!['page', 'section_id', 'sections'].includes(key) && !next.searchParams.has(key)) next.searchParams.append(key, value);
      }
      next.searchParams.set('section_id', this.dataset.section);
      try {
        const response = await fetch(next, { signal: this.controller.signal });
        if (!response.ok) throw new Error('Page unavailable');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const incoming = html.querySelector('ul#product-grid');
        const nextControl = html.querySelector('zayix-collection-scroll');
        if (!incoming || !nextControl) throw new Error('Grid unavailable');
        if (!this.isConnected || this.pageKey !== window.location.pathname + window.location.search) return;
        const ids = new Set([...this.grid.children].map(item => item.querySelector('a[href*="/products/"]')?.getAttribute('href')));
        const items = [...incoming.children].filter(item => !ids.has(item.querySelector('a[href*="/products/"]')?.getAttribute('href')));
        if (!items.length) throw new Error('No additional products');
        items.forEach(item => {
          item.classList.add('scroll-trigger--cancel');
          item.querySelectorAll('img').forEach(image => { image.loading = 'lazy'; });
        });
        const scrollBefore = window.scrollY;
        this.grid.append(...items);
        this.grid.getBoundingClientRect();
        if (!this.restoring) window.scrollTo({ top: scrollBefore, behavior: 'instant' });
        this.pages++;
        this.dataset.next = nextControl.dataset.next || '';
        this.status.textContent = this.dataset.next ? this.dataset.ready : this.dataset.end;
        this.button.hidden = !this.dataset.next;
        if (!this.dataset.next) this.observer.disconnect();
      } catch (error) {
        if (error.name !== 'AbortError' && this.isConnected) {
          this.failed = true;
          this.status.textContent = this.dataset.error;
          this.observer.disconnect();
        }
      } finally {
        this.loading = false;
        this.button.disabled = false;
        this.grid.removeAttribute('aria-busy');
      }
      if (!this.failed && this.isConnected && this.dataset.next && !this.restoring) {
        const rect = this.getBoundingClientRect();
        if (rect.top < innerHeight + 600 && rect.bottom > -600) requestAnimationFrame(() => this.load());
        else this.observer.observe(this);
      }
    }
  }
  customElements.define('zayix-collection-scroll', ZayixCollectionScroll);
}

